import type React from "react";

/**
 * The one column grid every homepage panel shares — §17.10 of the
 * Plate & Hairline geometry spec ("the column grid, including the
 * extremes"). Before this file existed, each text+aside panel invented its
 * own ratio, gap, breakpoint and vertical anchor (Newsletter: lg,
 * minmax(0,1fr)_minmax(20rem,0.82fr), gap-9, items-end, max-width 1200;
 * About: md, minmax(0,0.9fr)_minmax(0,1.1fr), gap-12, items-stretch,
 * max-width 1200; the hero's promise panel: lg, 1.15fr/0.85fr, gap-10,
 * max-width 1440) — so no two panels' columns lined up when stacked, which
 * is the defect this module exists to close.
 *
 * §17.10's 1200–1599 row specifies 2 EQUAL columns, a 48px gutter, a 32px
 * margin and a container capped at 1440. This is that row, expressed once.
 * The breakpoint is the spec's own 1200px (not Tailwind's 1024px `lg`) via
 * an arbitrary variant, so the grid engages exactly where the spec's table
 * says it does.
 */

/** Shared outer container: centers content, caps at the §17.10 1440 ceiling,
 *  and steps its margin 16 → 24 → 32 the same way on every panel. Since the
 *  container/gutter tokens landed (`--tls-container-max`, `--tls-gutter` in
 *  app/globals.css) this is the sitewide `.tls-container` utility, so the
 *  homepage panels, every page-level wrapper, the nav row and the footer read
 *  ONE axis. The style export is kept (empty) so call sites need no change. */
export const PANEL_CONTAINER_CLASS = "tls-container";
export const PANEL_CONTAINER_STYLE: React.CSSProperties = {};

/** Two equal columns above 1200px, one column below it. Vertical anchor is
 *  always `items-start` — a panel that needs its aside to hug the bottom is
 *  a layout the row-based system doesn't have, and "items-end" was what
 *  produced the measured 1-row vertical offset between Newsletter and
 *  About. */
export const PANEL_GRID_CLASS =
  "grid grid-cols-1 gap-y-12 gap-x-12 min-[1200px]:grid-cols-2 min-[1200px]:items-start py-12 lg:py-16";

/**
 * For panels whose outer `<section>` still carries the sitewide `.tls-section`
 * / `.tls-section-quiet` vertical-rhythm class (globals.css, out of scope for
 * this surface — §21 does not own it): that class's shorthand `padding`
 * property also sets a DIFFERENT horizontal inset (`clamp(16px,4vw,40px)`)
 * than this module's. An inline style always wins over a class rule (see
 * PRODUCT.md's "inline styles beat utility classes"), so this neutralises
 * just the horizontal component and leaves the section's vertical rhythm
 * untouched, letting `PANEL_CONTAINER_CLASS`/`PANEL_CONTAINER_STYLE` on the
 * inner div be the single source of horizontal truth for every panel.
 */
export const PANEL_SECTION_RESET_STYLE: { paddingLeft: number; paddingRight: number } = {
  paddingLeft: 0,
  paddingRight: 0,
};

/** Just the horizontal step (16 → 24 → 32), for a full-bleed panel that
 *  needs the SAME left/right inset as every capped panel but isn't itself
 *  wrapped in `PANEL_CONTAINER_CLASS` (e.g. a sidebar+grid layout with no
 *  1440 cap). Pair with the panel's own vertical padding. */
export const PANEL_INSET_X_CLASS = "tls-inset-x";
